import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, CheckSquare, FileSignature, FileText, Kanban, Target, User, UserPlus } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useAuth } from "@/context/AuthContext";
import { api, unwrapArray } from "@/lib/api";
import { useAskPanel } from "@/components/ai/AskPanelContext";
import { AiIcon } from "@/components/ai/AiIcon";
import type { NavGroup, NavItem, PanelSection } from "./nav";

interface PaletteContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}

const PaletteContext = createContext<PaletteContextValue | null>(null);

export function usePalette() {
  const ctx = useContext(PaletteContext);
  if (!ctx) throw new Error("usePalette must be used inside <CommandPalette>");
  return ctx;
}

/** The things "+ Create" and the palette can start, limited to what the user may create */
export function useCreateActions() {
  const { hasPermission } = useAuth();
  return useMemo(() => {
    const list = [
      { key: "lead", label: "New lead", to: "/leads/new", icon: UserPlus, allowed: hasPermission("lead.create") },
      { key: "deal", label: "New deal", to: "/deals?create=true", icon: Kanban, allowed: hasPermission("deal.create") },
      { key: "quote", label: "New quote", to: "/quotes?create=true", icon: FileText, allowed: hasPermission("quote.create") },
      { key: "task", label: "New task", to: "/tasks?create=true", icon: CheckSquare, allowed: hasPermission("activity.create") },
    ];
    return list.filter((a) => a.allowed);
  }, [hasPermission]);
}

const RECORD_ICON: Record<string, React.ElementType> = { lead: UserPlus, deal: Kanban, account: Building2, contact: User, quote: FileText, proposal: FileSignature };

interface Props {
  groups: NavGroup[];
  admin: NavItem[];
  settingsSections: PanelSection[];
  usersSections: PanelSection[];
  children: React.ReactNode;
}

/** Ctrl+K (or ⌘K) opens a jump-to box for every page the user may open and the create actions */
export function CommandPalette({ groups, admin, settingsSections, usersSections, children }: Props) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const createActions = useCreateActions();
  const { available: askOn, openAsk } = useAskPanel();

  const toggle = useCallback(() => setOpen((o) => !o), []);

  // Records: what you typed is looked up on the server (leads, deals, accounts, contacts, quotes, proposals you may see)
  const [query, setQuery] = useState("");
  const [records, setRecords] = useState<{ type: string; id: string; title: string; subtitle: string; to: string }[]>([]);
  useEffect(() => {
    const q = query.trim();
    if (!open || q.length < 2) {
      setRecords([]);
      return;
    }
    let alive = true;
    const t = setTimeout(() => {
      api
        .get(`/api/Search?q=${encodeURIComponent(q)}`)
        .then((r) => alive && setRecords(unwrapArray(r)))
        .catch(() => alive && setRecords([]));
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [query, open]);
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  const pages = groups.flatMap((g) => g.items);
  const settingsLinks = [...settingsSections, ...usersSections].flatMap((s) => s.links);

  return (
    <PaletteContext.Provider value={{ open, setOpen, toggle }}>
      {children}
      <CommandDialog open={open} onOpenChange={setOpen} title="Search" description="Jump to a page or start something new">
        <CommandInput placeholder="Search records, jump to a page or type a command…" value={query} onValueChange={setQuery} />
        <CommandList>
          <CommandEmpty>Nothing matches that.</CommandEmpty>
          {askOn && query.trim().length >= 3 && (
            <CommandGroup heading="Ask AI">
              {/* The typed text is part of the value so the palette's filter always keeps it */}
              <CommandItem value={`ask ${query}`} onSelect={() => {
                  setOpen(false);
                  openAsk({ q: query.trim() });
                }}
              >
                <AiIcon className="size-4" />
                <span className="min-w-0 flex-1 truncate">Ask the CRM: “{query.trim()}”</span>
              </CommandItem>
            </CommandGroup>
          )}
          {records.length > 0 && (
            <CommandGroup heading="Records">
              {records.map((r) => {
                const Icon = RECORD_ICON[r.type] || FileText;
                return (
                  // The typed text is part of the value so the palette's own filter never hides a server match
                  <CommandItem key={`${r.type}-${r.id}`} value={`record ${query} ${r.type} ${r.title} ${r.id}`} onSelect={() => go(r.to)}>
                    <Icon className="size-4" />
                    <span className="min-w-0 flex-1 truncate">{r.title}</span>
                    <span className="ml-2 max-w-[45%] truncate text-xs text-muted-foreground">{r.subtitle}</span>
                    <span className="ml-2 rounded bg-muted px-1.5 text-[10.5px] font-medium capitalize text-muted-foreground">{r.type}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}
          {createActions.length > 0 && (
            <CommandGroup heading="Create">
              {createActions.map((a) => (
                <CommandItem key={a.key} value={`create ${a.label}`} onSelect={() => go(a.to)}>
                  <a.icon className="size-4" />
                  {a.label}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          <CommandSeparator />
          <CommandGroup heading="Go to">
            {pages.map((p) => (
              <CommandItem key={p.key} value={`go ${p.label}`} onSelect={() => go(p.to)}>
                <p.icon className="size-4" />
                {p.label}
              </CommandItem>
            ))}
            {admin.map((p) => (
              <CommandItem key={p.key} value={`go ${p.label}`} onSelect={() => go(p.to)}>
                <p.icon className="size-4" />
                {p.label}
              </CommandItem>
            ))}
          </CommandGroup>
          {settingsLinks.length > 0 && (
            <CommandGroup heading="Settings">
              {settingsLinks.map((l) => (
                <CommandItem key={l.to} value={`settings ${l.label}`} onSelect={() => go(l.to)}>
                  {l.label}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </PaletteContext.Provider>
  );
}
